import React, { FunctionComponent, SVGProps } from "react";
import { Button, CircularProgress, Typography, useTheme } from "@mui/material";
import { Box } from "@mui/system";
import { TimeElapsed } from "./TimeElapsed";

export const CommandButton = (props: {
  icon: FunctionComponent<
    SVGProps<SVGSVGElement> & { title?: string | undefined }
  >;
  text: string;
  onClick: (e: React.MouseEvent<HTMLElement>) => void;
  active?: boolean;
  disabled?: boolean;
  loading?: boolean;
  style?: {
    borderColor: string;
    backgroundColor: string;
  };
  timer?: boolean;
  countdownFrom?: number;
  onCompleteCountdown?: () => void;
  preserveTimerSpace?: boolean;
  ripple?: boolean;
  disableWhenActive?: boolean;
}) => {
  const onClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!props.disabled) {
      props.onClick(e);
    }
  };

  const chooseBackgroundColor = () => {
    if (props.active) {
      return "primary.main";
    }
    return props?.style?.backgroundColor
      ? props?.style?.backgroundColor
      : "background.paper";
  };
  const chooseTextColor = () => {
    if (props.active) {
      return "black";
    }
    return "white";
  };
  const chooseBorderColor = () => {
    return props?.style?.borderColor
      ? props?.style?.borderColor
      : "text.primary";
  };

  const onComplete = () => {
    if (props.onCompleteCountdown) {
      props.onCompleteCountdown();
    }
  };

  const showTimerSpace = props.timer || props.preserveTimerSpace;

  return (
    <Box display="flex" flexDirection={"column"} alignItems="center">
      <Box display="flex" flexDirection={"column"}>
        <Button
          onClick={onClick}
          disabled={
            props.loading ||
            props.disabled ||
            (props.active && props.disableWhenActive !== false)
          }
          sx={{
            height: 64,
            width: 64,
            borderRadius: 8,
            border: 1,
            backgroundColor: chooseBackgroundColor(),
            borderColor: chooseBorderColor(),
            position: "relative",
            WebkitTapHighlightColor: "transparent",
            "&.Mui-disabled": {
              backgroundColor: chooseBackgroundColor(),
              borderColor: chooseBorderColor(),
            },
            "&::after": props.ripple
              ? {
                  content: '""',
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  width: 32,
                  height: 32,
                  borderRadius: "50%",
                  border: "3px solid rgba(255,255,255,0.8)",
                  pointerEvents: "none",
                  animation: "commandRipple 2s infinite",
                }
              : undefined,
            "@keyframes commandRipple": {
              "0%": {
                transform: "translate(-50%, -50%) scale(0.8)",
                opacity: 0.6,
              },
              "100%": {
                transform: "translate(-50%, -50%) scale(1.8)",
                opacity: 0,
              },
            },
          }}
        >
          {props.loading ? (
            <CircularProgress size={22} sx={{ color: chooseTextColor() }} />
          ) : (
            <props.icon stroke={chooseTextColor()} style={{ color: chooseTextColor() }} />
          )}
        </Button>
      </Box>
      <Box mt={1} display="flex" flexDirection={"column"} justifyItems="center">
        <Typography
          sx={{ textAlign: "center"}}
          variant={"subtitle2"}
          fontWeight="600"
          color={props.loading || props.disabled ? "#D9DBE9" : "text.primary"}
        >
          {props.text}
        </Typography>
      </Box>
      <Box 
        height={24} 
        width="100%" 
        sx={{ 
          visibility: showTimerSpace ? "visible" : "hidden",
          position: "relative"
        }}
      >
        <Box 
          position="absolute" 
          top={0} 
          left={0} 
          right={0} 
          display="flex" 
          justifyContent="center"
        >
          {props.timer ? (
            <TimeElapsed
              countdownFrom={props.countdownFrom}
              onComplete={onComplete}
            />
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};
